/**
 * Safe Browser Storage & Environment Utilities
 * 
 * Provides fail-safe wrappers for localStorage, sessionStorage, crypto.randomUUID,
 * and matchMedia listeners. Prevents crashes on older tablet browsers (iPadOS < 15.4,
 * older Android WebViews, Safari in private mode, or iframes with storage restrictions).
 */

const memoryFallbackStorage = new Map<string, string>();
const memoryFallbackSession = new Map<string, string>();

/**
 * Check if a storage engine is accessible without throwing SecurityError.
 */
function isStorageAvailable(type: "localStorage" | "sessionStorage"): boolean {
  if (typeof window === "undefined") return false;
  try {
    const storage = window[type];
    const testKey = "__catchai_storage_probe__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

const hasLocalStorage = isStorageAvailable("localStorage");
const hasSessionStorage = isStorageAvailable("sessionStorage");

export const safeLocalStorage = {
  getItem(key: string): string | null {
    if (hasLocalStorage) {
      try {
        return window.localStorage.getItem(key);
      } catch {}
    }
    return memoryFallbackStorage.get(key) ?? null;
  },

  setItem(key: string, value: string): void {
    if (hasLocalStorage) {
      try {
        window.localStorage.setItem(key, value);
        return;
      } catch {}
    }
    memoryFallbackStorage.set(key, value);
  },

  removeItem(key: string): void {
    if (hasLocalStorage) {
      try {
        window.localStorage.removeItem(key);
      } catch {}
    }
    memoryFallbackStorage.delete(key);
  }
};

export const safeSessionStorage = {
  getItem(key: string): string | null {
    if (hasSessionStorage) {
      try {
        return window.sessionStorage.getItem(key);
      } catch {}
    }
    return memoryFallbackSession.get(key) ?? null;
  },

  setItem(key: string, value: string): void {
    if (hasSessionStorage) {
      try {
        window.sessionStorage.setItem(key, value);
        return;
      } catch {}
    }
    memoryFallbackSession.set(key, value);
  },

  removeItem(key: string): void {
    if (hasSessionStorage) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {}
    }
    memoryFallbackSession.delete(key);
  }
};

/**
 * Universal RFC 4122 v4 UUID generator.
 * Works seamlessly on laptops, modern smartphones, and older tablet devices
 * where crypto.randomUUID is not implemented or fails in unauthenticated iframes.
 */
export function safeRandomUUID(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40; // Version 4
      buf[8] = (buf[8] & 0x3f) | 0x80; // Variant 10
      const hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {}

  // High-entropy fallback for legacy tablet browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Safe MediaQuery listener subscription supporting both modern addEventListener
 * and legacy addListener (for WebKit on older iPads and Android tablets).
 */
export function subscribeMediaQuery(
  query: string,
  callback: (matches: boolean) => void
): () => void {
  if (typeof window === "undefined" || !window.matchMedia) {
    return () => {};
  }

  try {
    const mediaQuery = window.matchMedia(query);
    callback(mediaQuery.matches);

    const listener = (event: MediaQueryListEvent | MediaQueryList) => {
      callback(event.matches);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", listener);
      return () => mediaQuery.removeEventListener("change", listener);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(listener);
      return () => mediaQuery.removeListener(listener);
    }
  } catch (err) {
    console.warn("safeMatchMedia subscription warning:", err);
  }

  return () => {};
}
